/**
 * Utility functions for calculating risk assessment levels
 * from simulation risk factor data.
 */

import type {
  RiskAssessmentInfoInterface,
  ScreenRiskAssessmentInfoInterface,
} from 'store/simulation/types/reportInfo';
import type { RiskAssessmentSimulationSettings } from 'store/storageunits/types';
import type {
  UnitInfoCrusherInterface,
  UnitInterface,
} from 'store/units/types';
import { UnitTypes } from 'unitData/types';

export const SCREEN_RISK_FACTOR_CONFIG: Partial<
  Record<UnitTypes, { unitFactors: string[]; deckFactors: string[] }>
> = {
  [UnitTypes.CONVENTIONAL_SCREEN]: {
    unitFactors: [
      'factorMoisture',
      'factorClay',
      'factorFeederLoad',
      'factorFineSeparation',
      'factorInclination',
      'factorWetScreening',
    ],
    deckFactors: [
      'factorAreaLoad',
      'factorFractionLength',
      'factorGradingDisBedDepthScreeningMedia',
    ],
  },
  [UnitTypes.LINEAR_MOTION_SCREEN]: {
    unitFactors: [
      'factorMoisture',
      'factorClay',
      'factorFeederLoad',
      'factorFineSeparation',
      'factorInclination',
      'factorWetScreening',
    ],
    deckFactors: [
      'factorAreaLoad',
      'factorFractionLength',
      'factorGradingDisBedDepthScreeningMedia',
    ],
  },
  [UnitTypes.FREEFALL_SCREEN_SF]: {
    unitFactors: [
      'factorMoisture',
      'factorClay',
      'factorFeederLoad',
      'factorFineSeparation',
    ],
    deckFactors: [
      'factorAreaLoad',
      'factorFractionLength',
      'factorDisBedDepth',
    ],
  },
  [UnitTypes.FREEFALL_SCREEN_SS]: {
    unitFactors: [
      'factorMoisture',
      'factorClay',
      'factorFeederLoad',
      'factorFineSeparation',
    ],
    deckFactors: [
      'factorAreaLoad',
      'factorSeparation',
      'factorFractionLength',
      'factorDisBedDepth',
      'factorScreeningMedia',
    ],
  },
  [UnitTypes.GRIZZLY_SCREEN]: {
    unitFactors: ['factorMoisture', 'factorClay', 'factorCrusherLoad'],
    deckFactors: [
      'factorAreaLoad',
      'factorP98P51',
      'factorDisBedDepth',
      'factorScreeningMedia',
    ],
  },
};

export const RISK_FACTOR_LABELS: Record<string, string> = {
  factorMoisture: 'Moisture',
  factorClay: 'Clay',
  factorFeederLoad: 'Feeder load',
  factorCrusherLoad: 'Crusher load',
  factorLoad: 'Load',
  factorReductionRatio: 'Reduction ratio',
  factorP98P51: 'Probability to reach P98 - P51',
  factorP50P21: 'Probability to reach P50 - P21',
  factorP20P15: 'Probability to reach P20 - P15',
  factorCapThrow: 'Capacity / Throw',
  factorCapThrowCss: 'Capacity / Throw / CSS',
  factorClosedCircuit: 'Closed circuit',
  factorCss: 'CSS',
  factorLoadCss: 'Load / CSS',
  factorFinesInFeed: 'Fines in feed',
  factorRotorLoad: 'Rotor load',
  factorBiFlow: 'Bi-flow',
  factorRotorSpeed: 'Rotor speed',
  factorCap: 'Capacity',
  factorPsd: 'PSD',
  factorPsdBiFlow: 'PSD bi-flow',
  factorFineSeparation: 'Fine separation',
  factorInclination: 'Inclination',
  factorWetScreening: 'Wet screening',
  factorAreaLoad: 'Area load',
  factorFractionLength: 'Fraction length',
  factorGradingDisBedDepthScreeningMedia: 'Grading / dis. bed depth / media',
  factorDisBedDepth: 'Dis. bed depth',
  factorSeparation: 'Separation',
  factorScreeningMedia: 'Screening media',
};

type CrusherRiskFactorKey =
  | 'factorMoisture'
  | 'factorClay'
  | 'factorLoad'
  | 'factorReductionRatio'
  | 'factorFeederLoad'
  | 'factorP98P51'
  | 'factorP50P21'
  | 'factorP20P15'
  | 'factorCapThrow'
  | 'factorCapThrowCss'
  | 'factorClosedCircuit'
  | 'factorCss'
  | 'factorLoadCss'
  | 'factorFinesInFeed'
  | 'factorRotorLoad'
  | 'factorBiFlow'
  | 'factorRotorSpeed'
  | 'factorCap'
  | 'factorPsd'
  | 'factorPsdBiFlow';

export const SELECTABLE_CRUSHER_RISK_FACTOR_KEYS = [
  'factorP98P51',
  'factorP50P21',
  'factorP20P15',
] as const;

export type SelectableCrusherRiskFactorKey =
  (typeof SELECTABLE_CRUSHER_RISK_FACTOR_KEYS)[number];

type SelectableCrusherRiskFactorInclusionFlagKey =
  | 'factorP98P51IncludedInRiskAssessment'
  | 'factorP50P21IncludedInRiskAssessment'
  | 'factorP20P15IncludedInRiskAssessment';

export const SELECTABLE_CRUSHER_RISK_FACTOR_INCLUSION_FLAG_MAP: Record<
  SelectableCrusherRiskFactorKey,
  SelectableCrusherRiskFactorInclusionFlagKey
> = {
  factorP98P51: 'factorP98P51IncludedInRiskAssessment',
  factorP50P21: 'factorP50P21IncludedInRiskAssessment',
  factorP20P15: 'factorP20P15IncludedInRiskAssessment',
};

const CRUSHER_TOTAL_RISK_FACTOR_KEYS: Partial<
  Record<UnitTypes, CrusherRiskFactorKey[]>
> = {
  [UnitTypes.JAW_CRUSHER_CJ]: [
    'factorMoisture',
    'factorClay',
    'factorFeederLoad',
    'factorLoadCss',
    'factorReductionRatio',
    'factorP98P51',
    'factorP50P21',
    'factorP20P15',
  ],
  [UnitTypes.CONE_CRUSHER_CH]: [
    'factorMoisture',
    'factorClay',
    'factorFeederLoad',
    'factorCapThrowCss',
    'factorClosedCircuit',
    'factorReductionRatio',
    'factorCss',
    'factorP98P51',
    'factorP50P21',
    'factorP20P15',
  ],
  [UnitTypes.CONE_CRUSHER_CS]: [
    'factorMoisture',
    'factorClay',
    'factorFeederLoad',
    'factorCapThrowCss',
    'factorClosedCircuit',
    'factorReductionRatio',
    'factorCss',
    'factorP98P51',
    'factorP50P21',
    'factorP20P15',
  ],
  [UnitTypes.IMPACT_CRUSHER_CV]: [
    'factorMoisture',
    'factorClay',
    'factorFeederLoad',
    'factorRotorLoad',
    'factorBiFlow',
    'factorRotorSpeed',
    'factorCap',
    'factorClosedCircuit',
    'factorPsd',
    'factorPsdBiFlow',
  ],
};

export const isCrusherRiskAssessmentInfo = (
  info: RiskAssessmentInfoInterface | ScreenRiskAssessmentInfoInterface,
): info is RiskAssessmentInfoInterface => {
  return !('decks' in info);
};

export const isSelectableCrusherRiskFactorKey = (
  key: string,
): key is SelectableCrusherRiskFactorKey =>
  (SELECTABLE_CRUSHER_RISK_FACTOR_KEYS as readonly string[]).includes(key);

export const isSelectableCrusherRiskFactorIncluded = (
  riskAssessmentInfo: RiskAssessmentInfoInterface,
  factorKey: SelectableCrusherRiskFactorKey,
): boolean =>
  riskAssessmentInfo[
    SELECTABLE_CRUSHER_RISK_FACTOR_INCLUSION_FLAG_MAP[factorKey]
  ] !== false;

export const mergeSelectableCrusherRiskFactorInclusionFlags = (
  nextRiskAssessmentInfo:
    RiskAssessmentInfoInterface | ScreenRiskAssessmentInfoInterface | undefined,
  previousRiskAssessmentInfo:
    RiskAssessmentInfoInterface | ScreenRiskAssessmentInfoInterface | undefined,
):
  | RiskAssessmentInfoInterface
  | ScreenRiskAssessmentInfoInterface
  | undefined => {
  if (!nextRiskAssessmentInfo) {
    return previousRiskAssessmentInfo;
  }

  if (
    !previousRiskAssessmentInfo ||
    !isCrusherRiskAssessmentInfo(nextRiskAssessmentInfo) ||
    !isCrusherRiskAssessmentInfo(previousRiskAssessmentInfo)
  ) {
    return nextRiskAssessmentInfo;
  }

  const merged = { ...nextRiskAssessmentInfo };

  SELECTABLE_CRUSHER_RISK_FACTOR_KEYS.forEach((factorKey) => {
    const inclusionKey =
      SELECTABLE_CRUSHER_RISK_FACTOR_INCLUSION_FLAG_MAP[factorKey];
    const previousValue = previousRiskAssessmentInfo[inclusionKey];

    if (previousValue !== undefined) {
      merged[inclusionKey] = previousValue;
    }
  });

  return merged;
};

const productOf = (factors: (number | undefined)[]): number =>
  factors
    .filter((f): f is number => typeof f === 'number' && !isNaN(f))
    .reduce((acc, f) => acc * f, 1.0);

const minDeckProbability = (
  unitProduct: number,
  deckFactorsList: (number | undefined)[][],
): number | null => {
  if (deckFactorsList.length === 0) return null;
  const probs = deckFactorsList.map((df) => unitProduct * productOf(df));
  return Math.min(...probs) * 100;
};

const getDisabledCrusherRiskFactorKeys = (
  settings: RiskAssessmentSimulationSettings,
  equipmentType: UnitTypes,
  hasHighBin = true,
): Set<CrusherRiskFactorKey> => {
  const disabledKeys = new Set<CrusherRiskFactorKey>();

  const isCHorCS =
    equipmentType === UnitTypes.CONE_CRUSHER_CH ||
    equipmentType === UnitTypes.CONE_CRUSHER_CS;

  if (isCHorCS && !hasHighBin) {
    disabledKeys.add('factorFeederLoad');
  }

  if (!settings.simulatePeakCapacity) {
    if (equipmentType === UnitTypes.JAW_CRUSHER_CJ) {
      disabledKeys.add('factorLoadCss');
    }
    if (
      equipmentType === UnitTypes.CONE_CRUSHER_CH ||
      equipmentType === UnitTypes.CONE_CRUSHER_CS
    ) {
      disabledKeys.add('factorCapThrowCss');
      disabledKeys.add('factorCss');
    }
    if (equipmentType === UnitTypes.IMPACT_CRUSHER_CV) {
      disabledKeys.add('factorRotorLoad');
      disabledKeys.add('factorBiFlow');
      disabledKeys.add('factorRotorSpeed');
      disabledKeys.add('factorCap');
    }
  }

  if (!settings.simulatePSD) {
    if (equipmentType === UnitTypes.JAW_CRUSHER_CJ) {
      disabledKeys.add('factorReductionRatio');
      disabledKeys.add('factorP98P51');
      disabledKeys.add('factorP50P21');
      disabledKeys.add('factorP20P15');
    }
    if (
      equipmentType === UnitTypes.CONE_CRUSHER_CH ||
      equipmentType === UnitTypes.CONE_CRUSHER_CS
    ) {
      disabledKeys.add('factorClosedCircuit');
      disabledKeys.add('factorReductionRatio');
      disabledKeys.add('factorCss');
      disabledKeys.add('factorP98P51');
      disabledKeys.add('factorP50P21');
      disabledKeys.add('factorP20P15');
    }
    if (equipmentType === UnitTypes.IMPACT_CRUSHER_CV) {
      disabledKeys.add('factorClosedCircuit');
      disabledKeys.add('factorPsd');
      disabledKeys.add('factorPsdBiFlow');
    }
  }

  return disabledKeys;
};

export const getRiskAssessmentHasHighBin = (
  unit: Pick<UnitInterface, 'type' | 'unitInfo'>,
): boolean => {
  const isCHorCS =
    unit.type === UnitTypes.CONE_CRUSHER_CH ||
    unit.type === UnitTypes.CONE_CRUSHER_CS;

  if (!isCHorCS) {
    return true;
  }

  return Boolean(
    (unit.unitInfo as Partial<UnitInfoCrusherInterface>).highBin?.useHighBin,
  );
};

export const getDisplayedRiskFactorKeys = (
  settings: RiskAssessmentSimulationSettings,
  equipmentType: UnitTypes,
  hasHighBin = true,
): CrusherRiskFactorKey[] => {
  const disabledKeys = getDisabledCrusherRiskFactorKeys(
    settings,
    equipmentType,
    hasHighBin,
  );
  return (CRUSHER_TOTAL_RISK_FACTOR_KEYS[equipmentType] ?? []).filter(
    (key) => !disabledKeys.has(key),
  );
};

export const getDisplayedUnitRiskFactorKeys = (
  settings: RiskAssessmentSimulationSettings,
  unit: Pick<UnitInterface, 'type' | 'unitInfo'>,
): CrusherRiskFactorKey[] =>
  getDisplayedRiskFactorKeys(
    settings,
    unit.type,
    getRiskAssessmentHasHighBin(unit),
  );

const applyGuaranteeOverrides = (
  riskAssessmentInfo: RiskAssessmentInfoInterface,
  settings: RiskAssessmentSimulationSettings,
  equipmentType: UnitTypes,
  hasHighBin = true,
): RiskAssessmentInfoInterface => {
  const factors = { ...riskAssessmentInfo };

  const isCJ = equipmentType === UnitTypes.JAW_CRUSHER_CJ;
  const isCHorCS =
    equipmentType === UnitTypes.CONE_CRUSHER_CH ||
    equipmentType === UnitTypes.CONE_CRUSHER_CS;
  if (isCHorCS) {
    factors.factorCapThrow = 1.0;
  }

  if (isCJ && !settings.simulatePeakCapacity) {
    factors.factorLoad = 1.0;
  }

  getDisabledCrusherRiskFactorKeys(settings, equipmentType, hasHighBin).forEach(
    (key) => {
      factors[key] = 1.0;
    },
  );

  SELECTABLE_CRUSHER_RISK_FACTOR_KEYS.forEach((key) => {
    if (!isSelectableCrusherRiskFactorIncluded(riskAssessmentInfo, key)) {
      factors[key] = 1.0;
    }
  });

  return factors;
};

/**
 * Calculates the total probability from risk assessment factors.
 * Multiplies all factors together (factors are already in decimal form 0-1)
 * and converts the result to a percentage.
 *
 * @param riskAssessmentInfo - Object containing all risk assessment factors (as decimals 0-1)
 * @param settings - Simulation settings that override factors
 * @param equipmentType - Equipment type for guarantee logic
 * @returns Total probability as a percentage (0-100)
 */
const calculateTotalProbability = (
  riskAssessmentInfo:
    | RiskAssessmentInfoInterface
    | ScreenRiskAssessmentInfoInterface
    | null
    | undefined,
  settings: RiskAssessmentSimulationSettings,
  equipmentType: UnitTypes,
  hasHighBin = true,
): number | null => {
  if (!riskAssessmentInfo) return null;

  const screenConfig = SCREEN_RISK_FACTOR_CONFIG[equipmentType];
  if (screenConfig) {
    const info = riskAssessmentInfo as unknown as Record<string, unknown> & {
      decks: Record<string, Record<string, unknown>>;
    };
    const unitProduct = productOf(
      screenConfig.unitFactors.map((k) => info[k] as number | undefined),
    );
    const deckFactors = Object.values(info.decks).map((d) =>
      screenConfig.deckFactors.map((k) => d[k] as number | undefined),
    );
    return minDeckProbability(unitProduct, deckFactors);
  }

  if (!isCrusherRiskAssessmentInfo(riskAssessmentInfo)) return null;

  const effectiveFactors = applyGuaranteeOverrides(
    riskAssessmentInfo,
    settings,
    equipmentType,
    hasHighBin,
  );
  const factors = (CRUSHER_TOTAL_RISK_FACTOR_KEYS[equipmentType] ?? []).map(
    (key) => effectiveFactors[key],
  );

  const hasAny = factors.some(
    (f): f is number => typeof f === 'number' && !isNaN(f),
  );
  if (!hasAny) return null;

  return productOf(factors) * 100;
};

export const calculateUnitRiskProbability = (
  riskAssessmentInfo:
    | RiskAssessmentInfoInterface
    | ScreenRiskAssessmentInfoInterface
    | null
    | undefined,
  settings: RiskAssessmentSimulationSettings,
  unit: Pick<UnitInterface, 'type' | 'unitInfo'>,
): number | null =>
  calculateTotalProbability(
    riskAssessmentInfo,
    settings,
    unit.type,
    getRiskAssessmentHasHighBin(unit),
  );

/**
 * Maps a probability percentage to a risk assessment level.
 *
 * @param probability - Probability as a percentage (0-100)
 * @returns Risk level: 'minor', 'medium', 'critical', or null
 */
export const getRiskLevelFromProbability = (
  probability: number | null,
): 'minor' | 'medium' | 'critical' | null => {
  if (probability === null || isNaN(probability)) return null;

  // Lower probability = higher risk
  if (probability < 40) {
    return 'critical';
  } else if (probability <= 60) {
    return 'medium';
  } else {
    return 'minor';
  }
};

/**
 * Calculates the risk assessment level from risk assessment info.
 * Combines probability calculation and risk level mapping.
 *
 * @param riskAssessmentInfo - Object containing all risk assessment factors
 * @param settings - Simulation settings that override factors
 * @param equipmentType - Equipment type for guarantee logic
 * @returns Risk level: 'minor', 'medium', 'critical', or null
 */
export const calculateRiskAssessmentLevel = (
  riskAssessmentInfo:
    | RiskAssessmentInfoInterface
    | ScreenRiskAssessmentInfoInterface
    | null
    | undefined,
  settings: RiskAssessmentSimulationSettings,
  equipmentType: UnitTypes,
  hasHighBin = true,
): 'minor' | 'medium' | 'critical' | null => {
  const totalProbability = calculateTotalProbability(
    riskAssessmentInfo,
    settings,
    equipmentType,
    hasHighBin,
  );
  return getRiskLevelFromProbability(totalProbability);
};

export const calculateUnitRiskAssessmentLevel = (
  riskAssessmentInfo:
    | RiskAssessmentInfoInterface
    | ScreenRiskAssessmentInfoInterface
    | null
    | undefined,
  settings: RiskAssessmentSimulationSettings,
  unit: Pick<UnitInterface, 'type' | 'unitInfo'>,
): 'minor' | 'medium' | 'critical' | null =>
  calculateRiskAssessmentLevel(
    riskAssessmentInfo,
    settings,
    unit.type,
    getRiskAssessmentHasHighBin(unit),
  );

/**
 * Display properties for each risk level.
 */
export const RISK_DISPLAY = {
  critical: { label: 'Critical risk', color: '#FF2E2E', bgColor: '#FF2E2E4D' },
  medium: { label: 'Medium risk', color: '#FFAE57', bgColor: '#FFAE574D' },
  minor: { label: 'Minor risk', color: '#7FCC5B', bgColor: '#7FCC5B4D' },
} as const;
